/**
 * ## ItemProvider.tsx ##
 * This file contains ItemProvider component
 * @packageDocumentation
 */

import * as React from 'react';
import {
	NavigateFunction,
	useLocation,
	useNavigate,
	useParams,
	Link,
} from 'react-router-dom';

import { Location } from 'history';

import once from 'lodash/once';
import isEmpty from 'lodash/isEmpty';

import { AnySchema, ValidationError } from 'yup';

import { WithDeleted } from '@common/typescript/objects/WithDeleted';
import useRequest from '@common/react/hooks/useRequest';
import { BaseParams } from '@common/react/objects/BaseParams';
import { clearListFields, returnAfterSubmit } from '@common/react/utils/utils';
import { useRequestProviderContext } from '@common/react/components/RequestProvider/RequestProvider';
import useAbortController from '@common/react/hooks/useAbortController';
import { useModal } from '@common/react/components/Modal/ModalContextProvider';
import SimpleLoader from '@common/react/components/UI/Loaders/BounceLoader';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import { LoadingError } from '@common/react/components/UI/LoadingError/LoadingError';
import { getSearchParamsFromUrl, handleUrl } from '@common/react/utils/FIltersParamsFromUrl/FiltersParamsFromUrl';
import { ClearForSubmitDelegate, TransformAfterSaveDelegate, TransformAfterSaveParams } from '@common/react/objects/Core';

export { Mode } from '@common/react/objects/Core';

type ItemProviderPropsRequests = {
	/**
	 * load request name.
	 */
	loadRequest: string;
	/**
	 * save request name.
	 */
	saveRequest?: string;
} | {
	/**
	 * load request name.
	 */
	loadRequest?: string;
	/**
	 * save request name.
	 */
	saveRequest: string;
}

export type UpdatedItemProviderProps<T> = Omit<T, 'saveRequest' | 'loadRequest'> & ItemProviderPropsRequests;

/**
 * ItemProvider component props
 *
 * @interface ItemProviderProps
 * @template {WithDeleted} T - item type (load response type)
 * @template TResponse - response type (save response type)
 * @template TFilters - load params type
 */
export interface ItemProviderProps<T extends WithDeleted, TResponse = T, TFilters = BaseParams> {
	/**
	 * ReactElement to be wrapped in an ItemProvider context
	 */
	children: React.ReactNode | ((context: ItemProviderContext<T, TResponse, TFilters>) => React.ReactNode);
	/**
	 * Element ID. Used as load param
	 * @default try to fill by paramKey at path or by urlKey at search (if urlKey isn't empty)
	 */
	id?: number;
	/**
	 * Schema for checking element before saving.
	 *
	 * - Need to set a default value for the property that will be validated
	 */
	validationSchema?: AnySchema;
	/**
	 * Defines the default element if id < 0.
	 * Ignored if withoutAdd is set to true
	 */
	add?: Partial<T>;
	/**
	 * Defines the default element.
	 * Ignored if withoutAdd is set to false and id < 0
	 * @default undefined
	 */
	item?: T | undefined;
	/**
	 * load request name. ItemProvider requires either loadRequest or saveRequest
	 * @default ''
	 */
	loadRequest?: string;
	/**
	 * save request name. ItemProvider requires either loadRequest or saveRequest
	 * @default ''
	 */
	saveRequest?: string;
	/**
	 * Transform item before save on server, the result of this function call will be spread on item before send to server.
	 * In case you need to clear properties of array item, use clearListFields
	 *
	 * @example clear 'notForServer' field
	 * clearForSubmit={() => ({ notForServer: undefined })}
	 * @example clear 'notForServer' field inside 'items' array of item
	 * clearForSubmit={({ item, clearListFields }) => ({ items: clearListFields(item.items, ['notForServer']) })}
	 *
	 * @default () => {} - doesn't clear any field, all fields will be sent to server
	 */
	clearForSubmit?: ClearForSubmitDelegate<T>;
	/**
	 * transform item after save
	 *
	 * @example will update id prop of each item in items
	 * transformAfterSave={({ item, response, returnAfterSubmit }) => ({
	 *   ...item,
	 *   ...returnAfterSubmit(item, ['items'], response),
	 * })}
	 * @example override 'id' field after server response
	 * transformAfterSave={({ item }) => ({ ...item, id: -1 })}
	 * @example override 'fromServer' field after server response
	 * transformAfterSave={({ item, response }) => ({ ...item, fromServer: response.fromServer })}
	 * @example override all item fields with fields from server response
	 * transformAfterSave={({ item, response }) => ({ ...item, ...response })}
	 *
	 * @default ({ item }) => item - doesn't transform anything, since item contains id from server response,
	 * only id will be changed
	 */
	transformAfterSave?: TransformAfterSaveDelegate<T, TResponse>;
	/**
	 * error handling function
	 * @param error - error text
	 */
	onRequestError?: ((error: string) => void);
	/**
	 * get error message
	 * @param error - error text
	 */
	transformErrorMessage?: ((error: string) => string);
	/**
	 * error handling function for load request
	 * @param error - error text
	 * @default onRequestError
	 */
	onLoadRequestError?: ((error: string) => void);
	/**
	 * error handling function for save
	 * @param error - error text
	 * @default onRequestError
	 */
	onSaveRequestError?: ((error: string) => void);
	/**
	 * validation error handling function
	 * @param item - not valid element
	 * @param err - solved error
	 * @param error - original error object
	 */
	onValidationError?: ((item: T, err, error: ValidationError) => void);
	/**
	 * view mode
	 */
	readonly?: boolean;
	/**
	 * function to be called after load
	 * @param item - loaded item
	 * @param params - load params
	 */
	onLoad?: (item: T, params: TFilters) => void;
	/**
	 * load params
	 */
	additionalParams?: TFilters;
	/**
	 * function to be called after item change
	 * @param item - new item
	 */
	updateItem?: (item: T) => void;
	/**
	 * init load condition
	 * @default solved from item.id
	 */
	skipInitLoad?: boolean;
	/**
	 * init error
	 */
	error?: string;
	/**
	 * defines the default value if element id < 0
	 * @default false
	 */
	withoutAdd?: boolean;
	/**
	 * a function that handles the url after save
	 * @default defaultHandleUrlAfterSave
	 * @param options - {@link HandleUrlAfterSaveOptions}
	 */
	handleUrlAfterSave?: (options: HandleUrlAfterSaveOptions<T, TResponse>) => void;
	/**
	 * function to be called after save
	 * @param item - saved item
	 * @param response - request response
	 */
	onSave?: (item: T, response: TResponse) => void;
	/**
	 * time to live (ms) for cached response at RequestProvider if cache is available
	 */
	ttl?: number;
	/**
	 * callback to be called before load
	 */
	beforeLoad?: (params: TFilters & { id: number }) => void;
	/**
	 * handle or not found error at RequestProvider. used at ServerPageProvider
	 * @default false
	 */
	preventHandleNotFound?: boolean;
	/**
	 * used instead of internal _item. can be useful for example if you need to store state in a store
	 */
	syncItem?: T;
	/**
	 * used instead of internal _setItem. It is advisable to use it together with syncItem
	 */
	syncSetItem?: React.Dispatch<React.SetStateAction<T>>;
	/**
	 * show loader when item not defined or itemLoading is true
	 * @default true
	 */
	showLoader?: boolean;
	/**
	 * custom loader
	 * @default <Loader defaultLoader={<SimpleLoader />} type="pageLoader" />
	 */
	loader?: React.ReactNode;
	/**
	 * key to get id from url search params
	 * @default ''
	 */
	urlKey?: string;
	/**
	 * key to get id from url path (you should have 'page-path/:[paramKey]' in router)
	 * @default 'id'
	 */
	paramKey?: string;
	/**
	 * If you pass this flag, then after saving the item will be as it was during initialization
	 */
	resetAfterSave?: boolean;
}

/**
 * ItemProvider context state
 *
 * @interface ItemProviderContextState
 * @template {WithDeleted} T - item type (load response type)
 * @template TResponse - response type (save response type)
 */
export interface ItemProviderContextState<T extends WithDeleted, TResponse = T> {
	/**
	 * stored element
	 */
	item: T;
	/**
	 * loading state for ItemProvider requests (save, update, delete), it will be true if itemProvider making that request
	 */
	loading: boolean;
	/**
	 * stored save or load error message
	 */
	error: string;
	/**
	 * loadRequest from props
	 */
	loadRequest: string;
	/**
	 * view mode
	 */
	readonly: boolean;
	/**
	 * Schema for checking element before saving.
	 *
	 * - Need to set a default value for the property that will be validated
	 */
	validationSchema?: AnySchema;
	/**
	 * loading state for load request, it will be true, if ItemProvider try to load item
	 */
	itemLoading: boolean;
	/**
	 * success message state
	 */
	message: string;
	/**
	 * a function that converts an element after saving,
	 * for example returns not send props of item or reset item
	 */
	transformAfterSave: (params: Omit<TransformAfterSaveParams<T, TResponse>, 'returnAfterSubmit'>) => T;
	/**
	 * If you pass this flag, then after saving the item will be as it was during initialization
	 */
	resetAfterSave?: boolean;
}

interface LoadOptions {
	silent?: boolean;
	// used at ServerPageProvider
	preventHandleNotFound?: boolean;
}

/**
 * ItemProvider context actions
 *
 * @interface ItemProviderContextActions
 * @template T - item type (load response type)
 * @template TResponse - response type (save response type)
 * @template TFilters - load params type
 */
interface ItemProviderContextActions<T, TResponse = T, TFilters = BaseParams> {
	/**
	 * load new item for ItemProvider
	 * @param params - load params
	 * @param options - LoadOptions
	 * @returns promise from load request
	 */
	load: (params?: TFilters, options?: LoadOptions) => Promise<T>;
	/**
	 * send save request
	 * @param item - new item
	 * @param skipValidation - ignore validation or no. By default is undefined
	 * @param requestName
	 * @returns promise from save request
	 */
	update: (item: T, skipValidation?: boolean, requestName?: string, preventHandleUrl?: boolean) => Promise<TResponse>;
	/**
	 * send delete request
	 */
	deleteItem: () => void;
	/**
	 * update stored item without request
	 */
	setItem: React.Dispatch<React.SetStateAction<T>>;
	/**
	 * update message state
	 */
	setMessage: React.Dispatch<React.SetStateAction<string>>;
	/**
	 * update error state
	 */
	setError: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * ItemProvider context
 *
 * @interface ItemProviderContext
 * @template {WithDeleted} T - item type (load response type)
 * @template TResponse - response type (save response type)
 * @template TFilters - load params type
 */
export interface ItemProviderContext<T extends WithDeleted, TResponse = T, TFilters = BaseParams> {
	/*
	 * ItemProvider context state
	 */
	state: ItemProviderContextState<T, TResponse>;
	/*
	 * ItemProvider context actions
	 */
	actions: ItemProviderContextActions<T, TResponse, TFilters>;
}

export const createItemProviderContext = once(<T extends WithDeleted, TResponse = T, TFilters = BaseParams>() =>
	React.createContext({} as ItemProviderContext<T, TResponse, TFilters>));

/**
 * get ItemProviderContext
 *
 * @template {WithDeleted} T - item type (load response type)
 * @template TResponse - response type (save response type)
 * @template TFilters - load params type
 * @param required - if true throw exception when context is empty
 * @returns ItemProviderContext object
 */
export const useItemProviderContext = <T extends WithDeleted, TResponse = T, TFilters = BaseParams>(
	required: boolean = true,
) => {
	const context: ItemProviderContext<T, TResponse, TFilters> = React.useContext(
		createItemProviderContext<T, TResponse, TFilters>(),
	);

	if (required && isEmpty(context)) throw 'Need ItemProvider context!';

	return context;
};

/**
 * HandleUrlAfterSaveOptions - options for HandleUrlAfterSave
 *
 * @template {WithDeleted} T - item type (load response type)
 * @template TResponse - response type (save response type)
 * @param item - element
 * @param response - request response
 * @param navigate - react-router navigate
 * @param location - react-router location
 * @param urlKey - key for id at url search
 */
interface HandleUrlAfterSaveOptions<T extends WithDeleted, TResponse = T> {
	item: T,
	response: TResponse,
	navigate: NavigateFunction,
	location: Location,
	urlKey?: string;
}

/**
 * defaultHandleUrlAfterSave - change item id in url (expects that new item id is "/-1")
 *
 * @template {WithDeleted} T - item type (load response type)
 * @template {WithDeleted} TResponse - response type (save response type)
 * @param options - {@link HandleUrlAfterSaveOptions}
 */
const defaultHandleUrlAfterSave = <T extends WithDeleted, TResponse extends WithDeleted = T>(
	options: HandleUrlAfterSaveOptions<T, TResponse>,
) => {
	const {
		item,
		response,
		navigate,
		location,
		urlKey,
	} = options;
	if (item.id < 0) {
		window.setTimeout(() => {
			if (urlKey) {
				const filters = {
					[urlKey]: response.id,
				};

				handleUrl(filters, location, navigate, response.id, '', true);
			} else {
				navigate({
					...location,
					pathname: location.pathname.replace('/-1', `/${response.id}`),
				}, { replace: true });
			}
		}, 0);
	}
};

/**
 * max id value server support
 */
const MaxIdValue = 2 ** 64;

/**
 * check that id isn't correct id: negative, or null, or nan, or bigger them MaxIdValue
 *
 * @param id - any that wants to be an id
 * @returns true if id invalid, else false
 */
const checkIdNotCorrect = (id: any) => +id < 0 || id === null || isNaN(+id) || +id > MaxIdValue;

/**
 * ItemProvider component.
 *
 * when the id from the properties is not defined, the id will be obtained from the URL
 * so if item don't have id and need prevent settings id from url (ItemProvider should only save item) pass id={-1}
 * if itemProvider should load item by some other params (path for example) use id={0}
 *
 * @example ItemProvider for item-editor-page/:id without ServerPageProvider:
 * <ItemProvider<T> loadRequest="loadRequest" saveRequest="saveRequest">{(context) => React.ReactNode}</ItemProvider>
 *
 * @example ItemProvider for item-editor-page?id= without ServerPageProvider:
 * <ItemProvider<T> loadRequest="loadRequest" saveRequest="saveRequest" urlKey="id">{React.ReactNode}</ItemProvider>
 *
 * @example ItemProvider for item-editor-page/:id with ServerPageProvider, (SPP should return page with item):
 * <ServerPageProvider path="item-editor-page-path">
 *   {(page) => <ItemProvider<T, T, TFilters> item={page?.item} loadRequest="loadRequest" saveRequest="saveRequest">
 *     {React.ReactNode}
 *   </ItemProvider>}
 * </ServerPageProvider>
 *
 * @example ItemProvider for arbitrary case, when we have itemId and don't use url (for new item we use itemId = -1):
 * <ItemProvider<T> loadRequest="loadRequest" saveRequest="saveRequest" id={itemId}>{React.ReactNode}</ItemProvider>
 *
 * @example ItemProvider for save only (login form, recover form, etc.):
 * <ItemProvider<T> saveRequest="saveRequest" id={-1} add={{ email: '' }} resetAfterSave>
 *   {(context) => React.ReactNode}
 * </ItemProvider>
 *
 * @example ItemProvider for load item by some param not id (path for example), use additionalParams:
 * <ItemProvider<T, T, Filters> loadRequest="loadRequest" id={0} additionalParams={{ path }}>
 *   {(context) => React.ReactNode}
 * </ItemProvider>
 *
 * @template {WithDeleted} T - item type (load response type)
 * @template TFilters - load params type
 * @template TResponse - response type (save response type)
 * @param props - {@link ItemProviderProps}
 * @type {React.FC<ItemProviderProps>}
 * @returns React.ReactElement
 */
export const ItemProvider: (<T extends WithDeleted, TResponse extends WithDeleted = T, TFilters = BaseParams>(
	p: UpdatedItemProviderProps<ItemProviderProps<T, TResponse, TFilters>>
) => React.ReactElement<T>) = <T extends WithDeleted, TResponse extends WithDeleted = T, TFilters = BaseParams>(
	props: ItemProviderProps<T, TResponse, TFilters>,
) => {
	const location = useLocation();
	const navigate = useNavigate();
	const {
		urlKey = '',
		paramKey = 'id',
		id: idFromProps,
	} = props;
	const haveIdFromProps = idFromProps !== undefined;
	const { [paramKey]: idFromParams } = urlKey || haveIdFromProps ? {} : useParams<BaseParams>();
	const { [urlKey]: idFromSearch } = urlKey && !haveIdFromProps ? getSearchParamsFromUrl(location) : {};
	const id = haveIdFromProps
		? idFromProps
		: (urlKey ? (idFromSearch === '0' ? null : idFromSearch ?? -1) : (idFromParams === '0' ? null : idFromParams ?? -1));
	const {
		item = undefined,
		loadRequest = '',
		saveRequest = '',
		children,
		validationSchema,
		clearForSubmit = () => {},
		transformAfterSave = ({ item }) => item,
		onRequestError,
		onLoadRequestError = onRequestError,
		onSaveRequestError = onRequestError,
		onValidationError,
		onLoad,
		additionalParams = {} as TFilters,
		updateItem,
		skipInitLoad = (item && item?.id === undefined) || +(item?.id ?? -1) === +id,
		error: initError = '',
		add = {},
		withoutAdd = false,
		handleUrlAfterSave = defaultHandleUrlAfterSave,
		onSave,
		ttl,
		transformErrorMessage = (err) => err,
		beforeLoad,
		readonly = id > 0 ? !validationSchema : false,
		preventHandleNotFound = false,
		syncSetItem,
		syncItem,
		showLoader = true,
		loader = <Loader defaultLoader={<SimpleLoader />} type="pageLoader" />,
		resetAfterSave,
	} = props;
	const ItemContext = createItemProviderContext<T, TResponse, TFilters>();

	const { openErrorMessage } = useModal();

	const [innerItem, setInnerItem] = React.useState<T>(!withoutAdd && +id < 0 ? { id, ...add } as T : item as T);
	const _item = syncItem || innerItem;
	const [showErrorComponent, setShowError] = React.useState(false);
	const _setItem = (value: React.SetStateAction<T>) => {
		setShowError(false);
		setInnerItem(value);
		syncSetItem?.(value);
	};
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState<string>(initError);
	const [message, setMessage] = React.useState<string>('');
	const isIdCorrect = !checkIdNotCorrect(id);
	const [itemLoading, setItemLoading] = React.useState(isIdCorrect && (!item || !skipInitLoad));
	const data = React.useRef<number>(skipInitLoad ? id : 0);
	const request = useRequest();
	const requestContext = useRequestProviderContext();
	const [abortController, _] = useAbortController();

	React.useEffect(() => {
		if ((!_item || +id !== +data.current) && isIdCorrect) {
			setItemLoading(true);
			const oldId = data.current;
			data.current = +id;
			load(undefined, { preventHandleNotFound })
				.then(() => {
					setError((prev) => {
						if (prev) {
							data.current = oldId;
						}
						return prev;
					});
				})
				.catch((err) => (typeof err !== 'string' || !err?.includes('aborted')) && console.log(err));
		} else if (_item && +id !== +data.current && checkIdNotCorrect(id)) {
			setShowError(true);
		}
	}, [id]);

	React.useEffect(() => {
		if (skipInitLoad && _item && +id >= 0 && +id === _item.id && requestContext?.actions?.updateCache) {
			requestContext.actions.updateCache(
				loadRequest,
				{ ...additionalParams, id },
				item,
				ttl,
			);
		}
		return () => {
			abortController.abort();
		};
	}, []);

	const setItem = (value: React.SetStateAction<T>) => {
		_setItem((prev) => {
			const newItem = typeof value === 'function' ? value(prev) : value;
			data.current = newItem.id;
			updateItem && updateItem(newItem);
			return newItem;
		});
	};

	const load = (params?: TFilters, options?: LoadOptions): Promise<T> => {
		if (!loadRequest) {
			(openErrorMessage || console.error)('loadRequest is empty');
			return Promise.resolve(_item);
		}
		setError('');
		const loadParams = { ...additionalParams, ...params, id };
		beforeLoad && beforeLoad(loadParams);

		return request<T>(
			loadRequest,
			loadParams,
			{
				beforeRequest: () => !options?.silent && setItemLoading(true),
				ttl,
				signal: abortController.signal,
				preventHandleNotFound: options?.preventHandleNotFound || preventHandleNotFound,
			},
		)
			.then((item) => {
				setItem(item);
				!options?.silent && setItemLoading(false);
				onLoad && onLoad(item, loadParams);
				return item;
			}).catch((error) => {
				if (typeof error === 'string' && error.includes('aborted')) {
					throw error;
				}
				!options?.silent && setItemLoading(false);

				onLoadRequestError && onLoadRequestError(error);
				setError(transformErrorMessage(error));
				return item as T;
			});
	};

	const saveItem = (data: T, requestName: string = saveRequest, preventHandleUrl = false): Promise<TResponse> => {
		const item = { ...data, ...clearForSubmit({ item: data, clearListFields }) };

		setLoading(true);
		setError('');

		return request<TResponse>(requestName, item)
			.then((response) => {
				setItem((resetAfterSave ? _item : {
					...data,
					...transformAfterSave({
						item: { ...data, id: response.id },
						response,
						prevItem: _item,
						returnAfterSubmit,
					}),
				}));

				!preventHandleUrl && handleUrlAfterSave({
					item: data,
					response,
					navigate,
					location,
					urlKey,
				});
				onSave && onSave(item, response);
				return response;
			}).catch((error: string) => {
				onSaveRequestError && onSaveRequestError(error);
				setError(transformErrorMessage(error));

				throw error;
			}).finally(() => setLoading(false));
	};

	const update = (
		item: T,
		skipValidation?: boolean,
		requestName: string = saveRequest,
		preventHandleUrl = false,
	): Promise<TResponse> => {
		if (!item.deleted && validationSchema && !skipValidation) {
			return validationSchema?.validate(item, { abortEarly: false }).then(() => {
				return saveItem(item, requestName, preventHandleUrl);
			}).catch((err) => {
				if (err.inner) {
					const er = {};
					for (let i = 0; i < err.inner.length; i++) {
						er[err.inner[i].path] = err.inner[i].errors[0];
					}

					setError(err.message);

					onValidationError && onValidationError(item, er, err);
				} else {
					throw err;
				}
			}) as Promise<TResponse>;
		}

		return saveItem(item, requestName, preventHandleUrl);
	};

	const deleteItem = () => {
		_item && saveItem({ ..._item, deleted: true });
	};

	const value: ItemProviderContext<T, TResponse, TFilters> = {
		state: {
			item: _item,
			loading,
			error,
			loadRequest,
			validationSchema,
			readonly,
			itemLoading,
			message,
			transformAfterSave: (params) => transformAfterSave({
				...params,
				returnAfterSubmit,
				item: params.item && { ...params.item, id: params.response.id },
			}),
		},
		actions: {
			load,
			update,
			setItem,
			deleteItem,
			setMessage,
			setError,
		},
	};

	return (
		<ItemContext.Provider value={value}>
			{showLoader && (!_item || itemLoading || showErrorComponent)
				? itemLoading ? loader : (
					<div className="error-component" style={{ wordBreak: 'break-word' }}>
						<LoadingError
							className="text-center error-component__content"
							reload={loadRequest && isIdCorrect ? load : undefined}
							text={checkIdNotCorrect(id)
								? <>
									Can't find item with id:&nbsp;
									{id}
									<br />
									<Link to="/">Go to home page</Link>
								</>
								: undefined}
						/>
					</div>
				)
				: typeof children === 'function' ? children(value) : children}
		</ItemContext.Provider>
	);
};
